import { queryKeys } from "@/lib/query-keys";
import { useApiInstance } from "@/providers/api-provider";
import { ListUser } from "@/stores/user-store";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";

export function useAllUsers() {
  return useQuery({
    queryKey: queryKeys.allUsers,
    queryFn: async (): Promise<ListUser[]> => {
      const response = await useApiInstance.get("/api/v1/organizations/users/list-all");
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useMyOrganizations() {
  return useQuery({
    queryKey: queryKeys.myOrgs,
    queryFn: async (): Promise<{ id: string; name: string }[]> => {
      const response = await useApiInstance.get("/api/v1/organizations/my-orgs");
      return response.data.data || [];
    },
    select: (data) => [...data].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" })),
    staleTime: 5 * 60 * 1000,
  });
}

const fetchOrgRoles = async (orgId: string): Promise<string[]> => {
  const response = await useApiInstance.get(`/api/v1/organizations/${orgId}/roles`);
  return response.data.roles;
};

export function useOrgRoles(orgId: string) {
  return useQuery({
    queryKey: queryKeys.orgRoles(orgId),
    queryFn: () => fetchOrgRoles(orgId),
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useOrgsRoles(orgIds: string[]) {
  return useQueries({
    queries: orgIds.map((orgId) => ({
      queryKey: queryKeys.orgRoles(orgId),
      queryFn: () => fetchOrgRoles(orgId),
      staleTime: 5 * 60 * 1000,
    })),
    combine: (results) => ({
      isLoading: results.some((result) => result.isPending),
      isError: results.some((result) => result.isError),
      data: results
        .map((result) => result.data ?? [])
        .reduce((shared, roles) => shared.filter((role) => roles.includes(role)), results[0]?.data ?? []),
    }),
  });
}

export function useInvalidateUsers() {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.allUsers });
    queryClient.invalidateQueries({ queryKey: queryKeys.myOrgs });
  };
}

export function useRefreshUsers() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.allUsers }),
        queryClient.invalidateQueries({ queryKey: queryKeys.myOrgs }),
      ]);
      await Promise.all([
        queryClient.refetchQueries({ queryKey: queryKeys.allUsers }),
        queryClient.refetchQueries({ queryKey: queryKeys.myOrgs }),
      ]);
    },
  });
}
